import React from 'react';
import { useTranslation } from 'react-i18next';
import { ApplicationConfig } from '../../../../redux/application/application.types';
import Icon from '../../../../components/envago/Icon/Icon';
import Information, { InformationType } from '../../../../components/envago/Information/Information';
import JobComment from '../../../../components/envago/JobComment/JobComment';
import LabeledList from '../../../../components/envago/LabeledList/LabeledList';
import MeterReadingPlausibilityIndicator from '../../../../components/envago/MeterReadingPlausibilityIndicator';
import MeterReadingUnit from '../../../../components/envago/MeterReadingUnit/MeterReadingUnit';
import MeterReadingTarifPlain from '../../../../components/envago/MeterReadingTarifPlain/MeterReadingTarifPlain';
import OptionalLabel from '../../../../components/envago/OptionalLabel/OptionalLabel';
import ReadingValue from '../../../../components/envago/ReadingValue/ReadingValue';
import UploadImage from '../../../../components/envago/UploadImage/UploadImage';
import { ReviewEntry } from '../ImportExcel.types';
import { hasReadingValueValidationError, isReadingHintValid } from '../ImportExcel.utils';
import dayjs from 'dayjs';
import { useFieldVisibility } from '../../../../fieldVisibility/useFieldVisibility';

type Props = {
    entry: ReviewEntry;
    currentIndex: number;
    total: number;
    requireComment: boolean;
    requirePhoto: boolean;
    mergedConfig?: ApplicationConfig;
    onChange: (entry: ReviewEntry) => void;
};

export const ImplausibleReviewStep: React.FC<Props> = ({ entry, currentIndex, total, requireComment, requirePhoto, mergedConfig, onChange }) => {
    const { t } = useTranslation();
    const visibility = useFieldVisibility();
    const { group } = entry;
    const imagesForbidden = mergedConfig?.features?.imagesForbidden ?? false;
    const commentsEnabled = mergedConfig?.meterReading?.commentsEnabled ?? true;
    const imageOrCommentRequired = requirePhoto && requireComment && !imagesForbidden && commentsEnabled;
    const hasComment = isReadingHintValid(entry.comment);
    // Die Adresse stammt aus dem AUFTRAG und ist ausblendbar. Die Zählernummer
    // in der Zeile darunter stammt aus der Excel-Datei des Nutzers und bleibt
    // unangetastet — er hat sie selbst geliefert, und ohne sie könnte er die
    // Zeile seiner Datei nicht zuordnen.
    const address = [
        visibility.pick('meter.meterAddress.street', group.job?.meter?.meterAddress?.street),
        visibility.pick('meter.meterAddress.houseNumber', group.job?.meter?.meterAddress?.houseNumber),
        visibility.pick('meter.meterAddress.zip', group.job?.meter?.meterAddress?.zip),
        visibility.pick('meter.meterAddress.city', group.job?.meter?.meterAddress?.city),
    ]
        .filter(Boolean)
        .join(' ');

    const getValidationMessage = (row: typeof group.rows[0]): { type: InformationType; text: string } | null => {
        if (!hasReadingValueValidationError(row)) return null;

        const rowHasPhoto = row.taskId ? (entry.images[row.taskId] ?? []).length > 0 : false;
        const showPhotoRequiredError = requirePhoto && !imagesForbidden && !rowHasPhoto;
        const showCommentRequiredError = requireComment && commentsEnabled && !hasComment;

        if (showPhotoRequiredError && showCommentRequiredError) {
            return { type: InformationType.ERROR, text: t('jobs.details.warnings.imageOrCommentRequiredError') };
        }
        if (showPhotoRequiredError && !requireComment) {
            return { type: InformationType.ERROR, text: t('jobs.details.warnings.imageRequiredError') };
        }
        if (showCommentRequiredError && !requirePhoto) {
            return { type: InformationType.ERROR, text: t('jobs.details.warnings.hintRequiredError') };
        }

        return { type: InformationType.WARNING, text: t('jobs.details.warnings.validationWarningText') };
    };

    return (
        <div className="flex flex-col gap-4">
            <h3 className="text-base font-semibold text-gray-800">{t('management.import.review.progress', { current: currentIndex + 1, total })}</h3>
            <div>
                {/* Zurueckgespiegelte Nutzereingabe: dieser Wert stammt aus der
                    Excel-Datei, die der Nutzer gerade hochgeladen hat, nicht aus
                    dem Auftrag. Ein Leck ist es nicht — ihn zu verstecken wuerde
                    nur den Arbeitsablauf brechen (Entscheidung 8 der Spec). */}
                <div className="text-sm font-mono font-bold">{group.rows[0]?.zaehlernummer}</div>
                {address && <div className="text-sm text-gray-500">{address}</div>}
            </div>

            <div className="flex flex-col gap-4">
                {group.rows.map((row, index) => {
                    const validationMessage = getValidationMessage(row);
                    const taskImages = row.taskId ? entry.images[row.taskId] ?? [] : [];
                    const effectivePostDecimals = mergedConfig?.meterReading?.queryPostDecimals ? row.task?.postDecimals ?? 0 : 0;
                    const effectivePreDecimals = row.task?.preDecimals ?? 8;

                    // Ein Tarif ohne Ablesewert wird nicht übermittelt (enqueueRows
                    // filtert ihn) und bleibt offen. Datum und Wert wären hier leer
                    // — als "Invalid Date" und aufgefüllte Null gerendert — und der
                    // Foto-Button führte ins Leere, weil das Bild mit der Zeile
                    // verworfen würde. Deshalb nur Tarif plus Hinweis zeigen.
                    if (row.category === 'no_value') {
                        return (
                            <div key={row.taskId ?? index} className={index > 0 ? 'border-t border-gray-200 pt-4' : undefined}>
                                <LabeledList>
                                    <OptionalLabel label={t('management.import.review.tariffLabel')}>
                                        <span className={'text-sm'}>
                                            <MeterReadingTarifPlain rateName={row.task?.rateName} obis={row.task?.obis} />
                                        </span>
                                    </OptionalLabel>
                                </LabeledList>
                                <Information type={InformationType.INFO} className="mt-2 text-sm">
                                    {t('management.import.review.noValueHint')}
                                </Information>
                            </div>
                        );
                    }

                    return (
                        <div key={row.taskId ?? index} className={index > 0 ? 'border-t border-gray-200 pt-4' : undefined}>
                            <LabeledList>
                                <OptionalLabel label={t('management.import.review.tariffLabel')}>
                                    <span className={'text-sm'}>
                                        <MeterReadingTarifPlain rateName={row.task?.rateName} obis={row.task?.obis} />
                                    </span>
                                </OptionalLabel>
                                <OptionalLabel label={t('management.import.review.readingDateLabel')}>
                                    <span className={'text-sm'}>{dayjs(row.ablesedatum).format('DD.MM.YYYY')}</span>
                                </OptionalLabel>
                                <OptionalLabel label={t('management.import.review.readingValueLabel')}>
                                    <span className="inline-flex items-center flex-wrap text-sm">
                                        <ReadingValue
                                            value={row.ablesewert}
                                            preDecimals={effectivePreDecimals}
                                            postDecimals={effectivePostDecimals}
                                            maskPostDecimals={mergedConfig?.meterReading?.maskPostDecimals}
                                        />
                                        <MeterReadingUnit className="text-gray-600" obis={row.task?.obis} unit={row.task?.unit} />
                                        <MeterReadingPlausibilityIndicator
                                            readingValue={row.ablesewert ?? undefined}
                                            minimumValue={row.task?.readingValueValidation?.minimumValue}
                                            maximumValue={row.task?.readingValueValidation?.maximumValue}
                                        />
                                        {row.category === 'plausible' && (
                                            <Icon
                                                aria={{ 'aria-label': t('management.import.review.plausibleHint') }}
                                                path={Icon.Path.mdiCheck}
                                                className="h-5 text-green-500 ml-1"
                                            />
                                        )}
                                    </span>
                                </OptionalLabel>
                            </LabeledList>
                            {row.category === 'already_done' ? (
                                <Information type={InformationType.INFO} className="mt-2 text-sm">
                                    {t('management.import.review.alreadyDoneHint')}
                                </Information>
                            ) : (
                                <>
                                    {validationMessage && (
                                        <Information type={validationMessage.type} className="mt-2 text-sm">
                                            {validationMessage.text}
                                        </Information>
                                    )}
                                    {!imagesForbidden && row.taskId && (
                                        <div className="mt-3">
                                            <UploadImage
                                                images={taskImages}
                                                onChange={(imgs) =>
                                                    onChange({
                                                        ...entry,
                                                        images: { ...entry.images, [row.taskId!]: imgs ?? [] },
                                                    })
                                                }
                                                onDelete={(imageIndex) =>
                                                    onChange({
                                                        ...entry,
                                                        images: {
                                                            ...entry.images,
                                                            [row.taskId!]: taskImages.filter((_, i) => i !== imageIndex),
                                                        },
                                                    })
                                                }
                                                text={t('management.import.review.photoLabel')}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    );
                })}
            </div>

            {commentsEnabled && (requireComment || imageOrCommentRequired) && (
                <div>
                    <OptionalLabel label={t('management.import.review.commentLabel')}>
                        <JobComment
                            key={group.jobId}
                            dropdownPosition="relative"
                            onChange={(comment) => onChange({ ...entry, comment: comment ?? '' })}
                            additionalComments={mergedConfig?.meterReading?.defaultComments}
                            // Always allow free text here, regardless of the tenant's
                            // freeTextCommentEnabled setting: an imported Ablesehinweis is
                            // arbitrary free text from the Excel file, and disabling this
                            // option would silently hide it (initialValue is only shown via
                            // the free-text field) even though it's still submitted.
                            isFreeTextOptionEnabled={true}
                            initialValue={entry.comment}
                        />
                    </OptionalLabel>
                </div>
            )}
            <div className={'border-t pt-2'}>
                <p className="text-xs text-gray-500">{t('management.import.review.skipAllHint')}</p>
            </div>
        </div>
    );
};
